import { useState, useEffect, useCallback } from 'react'
import AuthService from 'services/auth'
import cutileiApi from 'services/cutileiApi'
import PayoutService from 'services/Payout'
import { logApiError } from 'services/apiError'
import Summary from '../Summary'
import PayoutDetailsModal from '../Modals/PayoutDetailsModal'
import Select from 'components/Inputs/Select'
import DateInput from 'components/Inputs/DateInput'
import SecondaryButton from 'components/Buttons/SecondaryButton'
import { ReactComponent as Loading } from 'icons/loading2.svg'
import { FaHandHoldingUsd, FaExclamationTriangle, FaUserSlash } from 'react-icons/fa'
import { DateTime } from 'luxon'

import {
  Content,
  Card,
  CardHeader,
  CardTitle,
  FilterContainer,
  ShortcutRow,
  Shortcut,
  PayoutList,
  PayoutRow,
  PayoutInfo,
  PayoutMeta,
  PayoutSide,
  StatusBadge,
  EmptyState,
  MoneyText,
  InfoText,
  WarningText
} from '../styles'

const PER_PAGE = 50

/*
  O razão do repasse: o que a Cutilei deve a todos os titulares, com o que já foi pago.

  Sem auto-escopo, ao contrário da tela do salão — é justamente a visão agregada que permite
  conferir o razão contra o que foi pago por Pix manual antes da automação.

  Os dois atalhos do topo são os recortes que a operação olha todo dia: a fila de revisão (o
  repasse parou porque não foi possível saber se a transferência saiu, e só uma pessoa resolve)
  e os bloqueados (devidos, mas o titular ainda não tem conta ativa para receber). A fila de
  revisão deve viver curta — se ela crescer, é sinal de integração instável, não de trabalho
  acumulado.
*/
export default function Ledger () {
  const [loading, setLoading] = useState (true)
  const [loadingMore, setLoadingMore] = useState (false)
  const [payouts, setPayouts] = useState ([])
  const [meta, setMeta] = useState (null)
  const [error, setError] = useState (null)
  const [selected, setSelected] = useState (null)
  const [status, setStatus] = useState (PayoutService.STATUS_FILTERS[0])
  const [userType, setUserType] = useState (PayoutService.USER_TYPE_FILTERS[0])
  /* `[início, fim]` em ISO, como o `DateInput` em modo intervalo devolve. */
  const [period, setPeriod] = useState ([null, null])
  /* `null`, `'review'` ou `'blocked'`. Atalho escolhido substitui o filtro de situação. */
  const [shortcut, setShortcut] = useState (null)

  const [startDate, endDate] = period ?? [null, null]

  const requestConfig = AuthService.getRequestAuth ()

  const selectStyles = { width: 220 }

  const buildQuery = useCallback ((page, filters) => {
    const query = new URLSearchParams ({ page, per_page: PER_PAGE })

    /*
      O atalho manda no recorte: a fila de revisão é `status=failed` e os bloqueados são
      `blocked=true`, que já traz o próprio `status=pending`. Somar o seletor de situação a
      eles devolveria lista vazia sem explicar por quê.
    */
    if (filters.shortcut === 'review') query.set ('status', 'failed')
    else if (filters.shortcut === 'blocked') query.set ('blocked', 'true')
    else if (filters.status?.value) query.set ('status', filters.status.value)

    if (filters.userType?.value) query.set ('user_type', filters.userType.value)
    if (filters.startDate) query.set ('start_date', filters.startDate)
    if (filters.endDate) query.set ('end_date', filters.endDate)

    return query.toString ()
  }, [])

  const load = useCallback (async (page = 1, filters) => {
    try {
      if (page === 1) setLoading (true)
      else setLoadingMore (true)

      const { data } = await cutileiApi.get (
        `/admin/payouts?${buildQuery (page, filters)}`, requestConfig
      )

      const rows = PayoutService.normalizeList (data.data ?? [])

      setPayouts (prev => page > 1 ? [...prev, ...rows] : rows)
      setMeta (data.meta ?? null)
      setError (null)
    } catch (error) {
      logApiError (error)
      setError (error.response?.data?.message ?? 'Não foi possível carregar os recebíveis')
    } finally {
      setLoading (false)
      setLoadingMore (false)
    }
  }, [buildQuery, requestConfig])

  useEffect (() => {
    load (1, { status, userType, startDate, endDate, shortcut })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status, userType, startDate, endDate, shortcut])

  const loadMore = () => {
    if (!meta || meta.current_page >= meta.last_page) return

    load (meta.current_page + 1, { status, userType, startDate, endDate, shortcut })
  }

  /* Tocar no atalho ativo o desliga, devolvendo a lista ao filtro de situação. */
  const toggleShortcut = value => setShortcut (prev => prev === value ? null : value)

  /*
    Depois de cancelar ou resolver, a linha é reconciliada na lista em vez de a página ser
    relida: recarregar perderia as páginas já carregadas e o lugar onde quem opera estava.
  */
  const handleUpdate = updated => {
    setPayouts (prev => prev.map (
      payout => payout.id === updated.id ? { ...payout, ...updated } : payout
    ))
  }

  const hasMore = meta && meta.current_page < meta.last_page

  return (
    <Content>
      <Summary
        filters={{ startDate, endDate }}
        requestConfig={requestConfig}
      />

      <Card>
        <CardHeader>
          <CardTitle>Recebíveis</CardTitle>
          {meta && (
            <InfoText>
              {`${meta.total} ${meta.total === 1 ? 'recebível' : 'recebíveis'}`}
            </InfoText>
          )}
        </CardHeader>

        <ShortcutRow>
          <Shortcut
            type='button'
            active={shortcut === 'review'}
            onClick={() => toggleShortcut ('review')}
          >
            <FaExclamationTriangle size={12}/>
            Em verificação
          </Shortcut>
          <Shortcut
            type='button'
            active={shortcut === 'blocked'}
            onClick={() => toggleShortcut ('blocked')}
          >
            <FaUserSlash size={12}/>
            Sem conta para receber
          </Shortcut>
        </ShortcutRow>

        <FilterContainer>
          <Select
            name='status'
            placeholder='Situação...'
            value={status}
            options={PayoutService.STATUS_FILTERS}
            isDisabled={Boolean (shortcut)}
            onChange={(_, option) => setStatus (option ?? PayoutService.STATUS_FILTERS[0])}
            containerStyles={selectStyles}
          />
          <Select
            name='userType'
            placeholder='Titular...'
            value={userType}
            options={PayoutService.USER_TYPE_FILTERS}
            onChange={(_, option) => (
              setUserType (option ?? PayoutService.USER_TYPE_FILTERS[0])
            )}
            containerStyles={selectStyles}
          />
          <DateInput
            value={period}
            selectRange={true}
            clearable={true}
            placeholder='Período do atendimento'
            onChange={setPeriod}
            style={{width: 250}}
          />
        </FilterContainer>
        <InfoText>
          O período filtra pela data do atendimento, e não pela data em que o dinheiro cai
        </InfoText>

        {error && <WarningText>{error}</WarningText>}

        {loading ? (
          <Loading style={{alignSelf: 'center', margin: '20px 0px'}}/>
        ) : payouts.length === 0 ? (
          <EmptyState>
            <FaHandHoldingUsd size={26}/>
            <b>Nenhum recebível neste recorte</b>
            <p>
              {shortcut === 'review'
                ? 'Nada em verificação — é onde esta fila deve viver'
                : shortcut === 'blocked'
                  ? 'Nenhum recebível parado esperando conta do titular'
                  : 'Nenhum recebível com estes filtros'}
            </p>
          </EmptyState>
        ) : (
          <PayoutList>
            {payouts.map (payout => (
              <PayoutRow
                key={payout.id}
                onClick={() => setSelected (payout)}
              >
                <PayoutInfo>
                  <b>{payout.business?.name ?? 'Salão'}</b>
                  <PayoutMeta>
                    <span>{PayoutService.getUserTypeLabel (payout.userType)}</span>
                    <span>
                      {`Atendimento ${DateTime.fromISO (payout.scheduleDate).toFormat ('dd/MM/yyyy')}`}
                    </span>
                    {payout.payableAt && (
                      <span>
                        {`Paga ${DateTime.fromISO (payout.payableAt).toFormat ('dd/MM/yyyy')}`}
                      </span>
                    )}
                    {payout.anticipated && <span>Antecipado</span>}
                  </PayoutMeta>
                </PayoutInfo>
                <PayoutSide>
                  <MoneyText value={payout.netAmount}/>
                  <StatusBadge color={PayoutService.getStatusColor (payout.status)}>
                    {PayoutService.getStatusLabel (payout.status)}
                  </StatusBadge>
                </PayoutSide>
              </PayoutRow>
            ))}
          </PayoutList>
        )}

        {hasMore && (
          <SecondaryButton
            label='Carregar mais'
            loading={loadingMore}
            onClick={loadMore}
            color='#606060'
            width={160}
            height={34}
            style={{alignSelf: 'center'}}
          />
        )}
      </Card>

      {selected && (
        <PayoutDetailsModal
          visible={Boolean (selected)}
          payout={selected}
          requestConfig={requestConfig}
          onUpdate={handleUpdate}
          onClose={() => setSelected (null)}
        />
      )}
    </Content>
  )
}
